import { CartItem, TargetingFilterType, WidgetSettings } from '@gsc/types'
import { Targeting } from '../targeting/targeting'
import { Platform } from './index'
import Emitter from '../../shared/emitter'
import { parseQuery } from '../../shared/url'
import { ITargetingEnv } from '../targeting'

//todo: too large file - move to the separate module shopify/...
const envKey = [
  // on product page
  TargetingFilterType.ProductId,
  TargetingFilterType.ProductType,
  TargetingFilterType.ProductVendor,
  TargetingFilterType.ProductTag,

  TargetingFilterType.VariantId,
  TargetingFilterType.VariantPrice,
  TargetingFilterType.VariantAvailable,
  TargetingFilterType.VariantOptions,

  // in cart
  TargetingFilterType.CartTotalPrice,
  TargetingFilterType.CartDiscount,
  TargetingFilterType.CartItemCount,
  TargetingFilterType.CartProduct,
  TargetingFilterType.CartProductType,
  TargetingFilterType.CartVariant,

  TargetingFilterType.AddToCart,
  TargetingFilterType.RemoveFromCart,

  // about logged in customer
  TargetingFilterType.CustomerId,
  TargetingFilterType.CustomerTag,
  TargetingFilterType.CustomerOrderCount,
  TargetingFilterType.CustomerTotalSpent,
  TargetingFilterType.CustomerAcceptsMarketing,
]

const cartKeys = [
  TargetingFilterType.CartTotalPrice,
  TargetingFilterType.CartDiscount,
  TargetingFilterType.CartItemCount,
  TargetingFilterType.CartProduct,
  TargetingFilterType.CartProductType,
  TargetingFilterType.CartVariant,

  TargetingFilterType.AddToCart,
  TargetingFilterType.RemoveFromCart,
]

const extendedProductKeys = [
  TargetingFilterType.ProductTag,
  TargetingFilterType.VariantOptions,
  TargetingFilterType.VariantAvailable,
]

const customerKeys = [
  TargetingFilterType.CustomerTag,
  TargetingFilterType.CustomerOrderCount,
  TargetingFilterType.CustomerTotalSpent,
  TargetingFilterType.CustomerAcceptsMarketing,
]

interface ShopifyCartItem {
  product_id: number
  product_type?: string
  variant_id: number
}

interface ShopifyCart {
  total_price: number
  items_subtotal_price: number
  total_discount: number
  item_count: number
  items: ShopifyCartItem[]
}

function compareShopifyCarts(cart1: ShopifyCart, cart2: ShopifyCart): boolean {
  return JSON.stringify(cart1) !== JSON.stringify(cart2)
}

function cartFromShopifyApi(data: any): ShopifyCart {
  data.total_price /= 100
  data.items_subtotal_price /= 100
  data.total_discount /= 100
  return data
}

/**
 * Recursively extracts all string values from an object, ignoring keys
 * @param obj - The object to extract values from
 * @param ignoreKeys - Optional array of property keys whose values should be ignored
 */
function extractAllValues(obj: unknown, ignoreKeys: string[] = []): string[] {
  if (obj === null || obj === undefined) {
    return []
  }

  if (typeof obj === 'string') {
    return [obj]
  }

  if (Array.isArray(obj)) {
    return obj.flatMap(item => extractAllValues(item, ignoreKeys))
  }

  if (typeof obj === 'object') {
    return Object.entries(obj).flatMap(([key, value]) => {
      // Skip this value if the key is in the ignore list
      return ignoreKeys.includes(key) ? [] : extractAllValues(value, ignoreKeys)
    })
  }

  return []
}

const SHOPIFY_VARIABLE_PATTERNS = {
  cart: /\$cart/,
  customer: /\$customer/,
  order: /\$order/,
  product: /\$(?:product|variant)/,
} as const

/**
 * Analyzes widget configurations to determine which Shopify data are needed
 * This enables selective preloading of only the required data for better performance
 */
export function detectShopifyVariables(
  widgets: WidgetSettings[],
): Record<keyof typeof SHOPIFY_VARIABLE_PATTERNS, boolean> {
  const allContent = widgets
    .flatMap(widget =>
      widget.pages.flatMap(page => {
        const pageData = 'data' in page ? page.data : 'root' in page ? page.root : {}
        return extractAllValues(pageData, ['id', 'css', 'type', 'className'])
      }),
    )
    .join(' ')

  return {
    cart: SHOPIFY_VARIABLE_PATTERNS.cart.test(allContent),
    customer: SHOPIFY_VARIABLE_PATTERNS.customer.test(allContent),
    order: SHOPIFY_VARIABLE_PATTERNS.order.test(allContent),
    product: SHOPIFY_VARIABLE_PATTERNS.product.test(allContent),
  }
}

interface ShopifyCustomer {
  id: number
  email: string
  phone: string
  tags: string[]
  first_name: string
  last_name: string
  name: string
  accepts_marketing: boolean
  orders_count: number
  total_spent: number
}

const enum PageType {
  Home = 'home',
  Collection = 'collection',
  Product = 'product',
  Cart = 'cart',
  Account = 'account',
  SearchResults = 'searchresults',
  Page = 'page',
  Article = 'article',
}

interface ShopifyVariantDef {
  id: number
  title: string
  display_name: string
  price: number
  available?: boolean
  sku: string
  barcode: string
  requires_shipping?: boolean
  taxable?: boolean
  weight?: number
  options?: { [key: string]: string }
}

interface ShopifyProductDef {
  id: number
  type: string
  vendor: string
  title: string
  tags?: string[]
  options?: string[]
  variants: ShopifyVariantDef[]
  variant?: ShopifyVariantDef
}

interface ShopifyMetaPage {
  pageType?: string
  customerId?: number
  resourceType?: string
  resourceId?: number
}

interface ShopifyMeta {
  currency?: string

  product?: ShopifyProductDef
  selectedVariantId?: string

  page: ShopifyMetaPage
}

interface ShopifyCheckoutDef {
  customer_id: number
  order_id: number
}

interface ShopifyOrder {
  id: number
  name: string
  email: string
  phone: string
  currency: string
  total_price: number
  subtotal_price: number
  total_discounts: number
  total_tax: number

  financial_status: string
  fulfillment_status: string

  customer_id: number
}

export class ShopifyPlatform extends Emitter implements Platform {
  public name = 'shopify'

  private cart: Promise<ShopifyCart>
  private cartEvents: Emitter = new Emitter()
  private customer: Promise<ShopifyCustomer | void>
  private ordersCache: { [key: number]: Promise<ShopifyOrder> } = {}
  private productsCache: { [key: string]: Promise<ShopifyProductDef> } = {}

  private cartTagsExists = false
  private customerTagsExists = false
  private orderTagsExists = false
  private productTagsExists = false

  private cartEnv: Partial<ITargetingEnv> = {}

  public static detect(ctx: any): boolean {
    return ctx.Shopify || ctx.ShopifyAnalytics
  }

  public async initialize(widgets: WidgetSettings[], targeting: Targeting): Promise<boolean> {
    const { cart, customer, order, product } = detectShopifyVariables(widgets)

    this.cartTagsExists = cart
    this.customerTagsExists = customer
    this.orderTagsExists = order
    this.productTagsExists = product

    const preloads: Array<Promise<any>> = []
    if (this.cartTagsExists) {
      preloads.push(this.getCart())
    }
    if (this.customerTagsExists) {
      preloads.push(this.getCustomer())
    }
    if (this.orderTagsExists) {
      const orderId = this.getOrderId()
      if (orderId) {
        preloads.push(this.getOrder(orderId))
      }
    }
    if (this.productTagsExists) {
      preloads.push(this.getProduct())
    }
    if (preloads.length) {
      await Promise.all(preloads)
    }

    this.cartEvents.on('init', (_, { newCart }: { newCart: ShopifyCart }) => {
      console.log('cart inited event', newCart)
      targeting.triggerChange()
    })

    this.cartEvents.on('change', (_, { newCart, prevCart }: { newCart: ShopifyCart; prevCart: ShopifyCart }) => {
      console.log('cart changed event', prevCart, newCart)

      const countDiff = newCart.item_count - prevCart.item_count
      if (countDiff < 0) {
        this.cartEnv[TargetingFilterType.RemoveFromCart] = true
      } else if (countDiff > 0) {
        this.cartEnv[TargetingFilterType.AddToCart] = true
      }

      // retargeting because cart was changed
      targeting.triggerChange()
    })

    // intercept ajax api - https://shopify.dev/api/ajax/reference/cart
    targeting.watchers.addAjaxWatcher(
      { method: 'post', url: '/cart/add(\\.js)?' },
      () => this.refreshCart(), // no cart in response, require cart reloading
    )
    targeting.watchers.addAjaxWatcher({ method: 'post', url: '/cart/(change|update|clear)(\\.js)?' }, req => {
      // catch change cart targeting
      if (req.response) {
        req.response.then(resp => {
          if (resp.status === 200) {
            this.refreshCart(cartFromShopifyApi(JSON.parse(resp.body)))
          }
        })
      } else {
        this.refreshCart()
      }
    })

    // refresh cart on external ajax refreshes
    targeting.watchers.addAjaxWatcher({ method: 'get', url: '/cart.js' }, req => {
      if (req.response) {
        req.response.then(resp => {
          if (resp.status === 200) {
            this.refreshCart(cartFromShopifyApi(JSON.parse(resp.body)))
          }
        })
      }
    })

    return true
  }

  public getEnvironmentKeys(): TargetingFilterType[] {
    return envKey
  }

  public async getEnvironment(keys: TargetingFilterType[]): Promise<Partial<ITargetingEnv>> {
    const customerId = this.getCustomerId()
    let env: Partial<ITargetingEnv> = {}

    if (customerId) {
      env[TargetingFilterType.CustomerId] = customerId.toString()
    }

    const requireExtendedProductDef = keys.some(k => extendedProductKeys.indexOf(k) !== -1)
    const product = await this.getProduct(requireExtendedProductDef)
    if (product) {
      env = {
        ...env,
        [TargetingFilterType.ProductId]: product.id.toString(),
        [TargetingFilterType.ProductType]: product.type,
        [TargetingFilterType.ProductVendor]: product.vendor,
        [TargetingFilterType.ProductTag]: product.tags,
      } as Partial<ITargetingEnv>
      if (product.variant) {
        env = {
          ...env,
          [TargetingFilterType.VariantId]: product.variant.id.toString(),
          [TargetingFilterType.VariantPrice]: product.variant.price,
          [TargetingFilterType.VariantAvailable]: product.variant.available,
          [TargetingFilterType.VariantOptions]: product.variant.options,
        } as Partial<ITargetingEnv>
      }
    }

    if (keys.some(k => cartKeys.indexOf(k) !== -1)) {
      const cart = await this.getCart()
      const cartItems = cart.items || []
      env = {
        ...env,
        [TargetingFilterType.CartTotalPrice]: cart.total_price,
        [TargetingFilterType.CartDiscount]: cart.total_discount,
        [TargetingFilterType.CartItemCount]: cart.item_count,
        [TargetingFilterType.CartProduct]: cartItems.map(i => i.product_id.toString()),
        [TargetingFilterType.CartProductType]: [...new Set(cartItems.map(i => i.product_type).filter(pt => pt))],
        [TargetingFilterType.CartVariant]: cartItems.map(i => i.variant_id.toString()),

        ...this.cartEnv,
      } as Partial<ITargetingEnv>
    }
    this.cartEnv = {} // reset cart triggers

    if (keys.some(k => customerKeys.indexOf(k) !== -1)) {
      const customer = await this.getCustomer()
      if (customer) {
        env = {
          ...env,
          [TargetingFilterType.CustomerId]: customer.id.toString(),
          [TargetingFilterType.CustomerTag]: customer.tags,
          [TargetingFilterType.CustomerTotalSpent]: customer.total_spent,
          [TargetingFilterType.CustomerOrderCount]: customer.orders_count,
          [TargetingFilterType.CustomerAcceptsMarketing]: customer.accepts_marketing,
        } as Partial<ITargetingEnv>
      }
    }

    return env
  }

  public async getTrackingContext(): Promise<{ [p: string]: any } | undefined> {
    const customerId = this.getCustomerId()
    const orderId = this.getOrderId()
    const product = await this.getProduct(false)
    const ctx: { [p: string]: any } = {}
    if (customerId) {
      ctx.customerId = customerId
    }
    if (orderId) {
      ctx.orderId = orderId
    }
    if (product) {
      ctx.productId = product.id
      if (product.variant) {
        ctx.variantId = product.variant.id
      }
    }
    return ctx
  }

  public async getTags(): Promise<{ [p: string]: any }> {
    let tags: { [p: string]: any } = {}

    // cart
    if (this.cartTagsExists) {
      const cart = await this.getCart()
      tags = {
        ...tags,
        cartItemCount: cart.item_count,
        cartTotalPrice: cart.total_price,
        cartDiscount: cart.total_discount,
        cartSubtotalPrice: cart.items_subtotal_price,
      }
    }

    // customer
    if (this.customerTagsExists) {
      const customer = await this.getCustomer()
      if (customer) {
        tags = {
          ...tags,
          customerId: customer.id,
          customerEmail: customer.email,
          customerPhone: customer.phone,
          customerName: customer.name,
        }
      }
    }

    // order
    if (this.orderTagsExists) {
      const orderId = this.getOrderId()
      if (orderId) {
        const order = await this.getOrder(orderId)
        if (order) {
          tags = {
            ...tags,
            orderId: order.id,
            orderName: order.name,
            orderTotalPrice: order.total_price,
            orderSubtotalPrice: order.subtotal_price,
          }
        }
      }
    }

    // product & variant
    if (this.productTagsExists) {
      const product = await this.getProduct(true)
      if (product) {
        tags = {
          ...tags,
          productId: product.id,
          productVendor: product.vendor,
          productType: product.type,
          productTitle: product.title,
        }
        // product variant
        if (product.variant) {
          tags = {
            ...tags,
            variantId: product.variant.id,
            variantPrice: product.variant.price,
            variantTitle: product.variant.title,
            variantSku: product.variant.sku,
            variantBarcode: product.variant.barcode,
          }
          for (const opt in product.variant.options) {
            tags[`variantOption${opt}`] = product.variant.options[opt]
          }
        }
      }
    }

    return tags
  }

  public pageView() {
    this.cartEnv = {}
  }

  public async getCurrentItem(): Promise<CartItem> {
    const product = await this.getProduct(false)
    if (product && product.variant) {
      return { id: product.variant.id, quantity: 1, label: product.variant.display_name }
    }
  }

  public async runAddToCart(items: CartItem[], redirect?: string): Promise<boolean> {
    // https://shopify.dev/docs/themes/ajax-api/reference/cart#post-cart-add-js
    const body = {
      items: items
        .filter(item => item.id)
        .map(item => {
          return {
            id: item.id,
            quantity: item.quantity,
          }
        }),
    }
    if (body.items.length < 0) {
      return false
    }

    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (resp.ok && redirect) {
      window.open(redirect, '_self')
    }
    return resp.ok
  }

  public async runApplyCoupon(coupon: string, redirect?: string): Promise<boolean> {
    if (!redirect) {
      return window.fetch(`/discount/${encodeURI(coupon)}`, { redirect: 'manual' }).then(() => true)
    } else {
      window.open(`/discount/${encodeURI(coupon)}?redirect=${redirect ? encodeURIComponent(redirect) : ''}`, '_self')
      return true
    }
  }

  private async refreshCart(cart: ShopifyCart = null) {
    let prevCart: ShopifyCart = null
    if (this.cart) {
      try {
        prevCart = await this.cart
      } catch (e) {
        console.warn(e)
        this.cart = null
      }
    }

    this.cart = cart
      ? Promise.resolve(cart) // cart from input argument
      : this.createCartPromise() // load cart ourself
    const newCart = await this.cart

    if (!prevCart) {
      this.cartEvents.trigger('init', { newCart })
    } else {
      // compare prev & new carts
      if (compareShopifyCarts(prevCart, newCart)) {
        this.cartEvents.trigger('change', { newCart, prevCart })
      }
    }
  }

  private createCartPromise = async () => {
    console.log('shopify: loading cart...')
    const resp = await fetch('/cart.js')
    if (resp.ok) {
      return cartFromShopifyApi(await resp.json())
    } else {
      throw new Error('Failed to load cart')
    }
  }

  private async getCart(): Promise<ShopifyCart> {
    if (!this.cart) {
      this.cart = this.createCartPromise()
    }
    return this.cart
  }

  private async createCustomerPromise(id: number): Promise<ShopifyCustomer> {
    // load current logged in customer with proxy
    const resp = await fetch(`/apps/getsitecontrol?view=customer&id=${id}`)
    if (resp.ok) {
      return (await resp.json()) as ShopifyCustomer
    }
  }

  private getShopifyCheckout(): ShopifyCheckoutDef | void {
    const context = window as any
    if (context.Shopify && context.Shopify.checkout) {
      return context.Shopify.checkout as ShopifyCheckoutDef
    }
  }

  private getCustomerId(): number | void {
    const checkout = this.getShopifyCheckout()
    if (checkout) {
      // if on order status page - use order customer id
      return checkout.customer_id
    }
    return this.getMetaPage().customerId
  }

  private getCustomer(): Promise<ShopifyCustomer | void> {
    // get current logged in customer
    if (!this.customer) {
      const customerId = this.getCustomerId()
      this.customer = customerId ? this.createCustomerPromise(customerId) : Promise.resolve()
    }
    return this.customer
  }

  private getOrderId(): number | void {
    const checkout = this.getShopifyCheckout()
    if (checkout) {
      return checkout.order_id
    }
  }

  private async getOrder(id: number): Promise<ShopifyOrder | void> {
    // get current logged in customer
    if (!this.ordersCache[id]) {
      this.ordersCache[id] = fetch(`/apps/getsitecontrol?view=order&id=${id}`).then(resp => {
        if (resp.ok) {
          return resp.json().then(data => {
            return data as ShopifyOrder
          })
        } else {
          return null
        }
      })
    }
    return await this.ordersCache[id]
  }

  private getMetaPage(): ShopifyMetaPage {
    const context = window as any
    let metaPage: ShopifyMetaPage = {
      pageType: null,
    }

    if (context.ShopifyAnalytics) {
      const sa = context.ShopifyAnalytics.meta
      if (sa && sa.page) {
        metaPage = { ...sa.page }
      }
    }

    if (!metaPage.pageType) {
      if (window.document.location.pathname === '/cart') {
        metaPage.pageType = PageType.Cart
      } else if (window.document.location.pathname === '/account') {
        metaPage.pageType = PageType.Account
      }
    }

    return metaPage
  }

  private getMeta(): ShopifyMeta {
    // collect page meta information
    const context = window as any

    const meta: ShopifyMeta = {
      page: this.getMetaPage(),
    }

    if (context.ShopifyAnalytics) {
      const sa = context.ShopifyAnalytics.meta
      if (sa) {
        meta.currency = sa.currency
        meta.selectedVariantId = sa.selectedVariantId
        if (sa.product) {
          meta.product = {
            id: sa.product.id,
            vendor: sa.product.vendor,
            type: sa.product.type,
            title: sa.product.variants[0].name.split(' - ').slice(0, -1).join(' - ').trim(),
            variants: sa.product.variants.map((v: any) => {
              return {
                id: v.id,
                title: v.public_title,
                sku: v.sku,
                price: v.price / 100,
              }
            }),
          }
        }
      }
    }

    return meta
  }

  private async getProduct(extended = false): Promise<ShopifyProductDef> {
    // collect current product info
    const meta = this.getMeta()

    let product = meta.product
    let productDef = null
    // search in html
    const jsonEl = window.document.getElementById('ProductJson-product-template')
    if (jsonEl) {
      productDef = JSON.parse(jsonEl.innerHTML)
    }

    if (!productDef && (extended || !product)) {
      // load from ajax api https://shopify.dev/docs/themes/ajax-api/reference/product
      const productHandlerMatch = window.document.location.pathname.match(/\/products\/([^/]+)$/)
      if (productHandlerMatch) {
        const productHandler = productHandlerMatch[1]
        if (!this.productsCache[productHandler]) {
          console.log(`shopify: loading extended product info by ${productHandler}...`)
          this.productsCache[productHandler] = fetch(`/products/${productHandler}.js`).then(async resp => {
            if (resp.ok) {
              return await resp.json()
            } else {
              return null
            }
          })
        }
        productDef = await this.productsCache[productHandler]
      }
    }

    if (productDef) {
      // extended product info
      product = {
        id: productDef.id,
        type: productDef.type,
        vendor: productDef.vendor,
        variants: [],
        // extended
        title: productDef.title,
        tags: productDef.tags,
        options: productDef.options,
      }
      productDef.variants.forEach((v: any) => {
        product.variants.push({
          id: v.id,
          title: v.public_title,
          display_name: v.name,
          price: v.price / 100,
          sku: v.sku,
          // extended
          options: product.options.reduce((s, o, ix) => {
            return { ...s, [o]: v.options[ix] }
          }, {}),
          barcode: v.barcode,
          weight: v.weight,
          available: v.available,
          taxable: v.taxable,
          requires_shipping: v.requires_shipping,
        })
      })
    }

    // guess selected variant
    const query = parseQuery(window.document.location.search)
    let selectedVariantId = query.variant

    if (!selectedVariantId) {
      selectedVariantId = meta.selectedVariantId
    }

    if (product && product.variants) {
      if (selectedVariantId) {
        for (let ix = 0; ix < product.variants.length; ix++) {
          if (product.variants[ix].id.toString() == selectedVariantId.toString()) {
            product.variant = product.variants[ix]
            break
          }
        }
      }
      if (!product.variant) {
        product.variant = product.variants[0]
      }
    }

    return product
  }
}
